import { FC, useMemo, useRef, useState } from 'react';
import { QcsLoadingButton } from '../../../common/basic/QcsLoadingButton';
import { Modal as QcsModal, useTheme } from '@mui/material';
import styled, { CSSObject } from '@emotion/styled';
import { parseISO } from 'date-fns';

import { QcsModalBox } from '../../../common/basic/QcsModalBox';
import { QcsTypography } from '../../../common/basic/QcsTypography';
import { QcsSimpleTable, QcsSimpleTableValue } from '../../../common/QcsSimpleTable';
import { SectionHeader } from '../../../common/SectionHeader';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { BASE_URL_WITH_VERSION, mediaApi } from '../../../../utils/api';
import { DocumentInputUploadModal } from '../../../risk/DocumentInputUploadModal';
import { useAppSelector } from '../../../../store';
import { getMediaToken } from '../../../../utils/storage';
import { InvitedPersonsDocumentDateModal, InvitedPersonsDocumentDateModalResult } from './InvitedPersonsDocumentDateModal';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { InnerTooltipWrapper, QcsTooltip } from '../../../common/basic/QcsTooltip';
import { setErrorSnacks } from '../../../../utils/error';
import { hasRole, isExternal } from '../../../../utils/roles';
import { selectIdentity } from '../../../../store/entities/identity';
import { getLangNameObj, invitationCertificateIsExpired } from '../../../../utils/format';
import { DateFormat } from '../../../../utils/date';
import { useInvitation } from './invitationFunctions';
import { ConfirmDialog } from '../../../common/ConfirmDialog';
import { ShowImageButton } from './ShowImageButton';
import { InvitedPersonDto } from '@api/models/invitation/InvitedPersonDto';
import { UserRole } from '@api/models/enums/UserRole';
import { InvitationState } from '@api/models/enums/InvitationState';
import { InvitationDocumentResponse } from '@api/models/invitation/InvitationDocumentResponse';
import { FileUploadMediaResponse } from '@api/models/invitation/FileUploadMediaResponse';
import { invitationService } from '@api/services/services';
import { resources } from '@api/resources';

const SubRowInfo = styled.div((): CSSObject => ({
  display: 'flex',
  alignItems: 'center',
  gap: '1.5rem',
  flex: 1,
  overflow: 'hidden',
}));

const SubRowActions = styled.div((): CSSObject => ({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
}));

interface Props {
  data?: InvitedPersonDto;
  onClose: () => void;
}

export const InvitedPersonsDocumentModal: FC<Props> = ({ data, onClose }) => {
  const { t, i18n, invitation, callWithLock, reloadInvitation, enqueueErrorSnackbar, isSaving, setIsSaving } = useInvitation();

  const [uploadModal, setUploadModal] = useState<InvitationDocumentResponse>();
  const [dateModal, setDateModal] = useState(false);
  const [validTo, setValidTo] = useState<string>();
  const [certificateTypeId, setCertificateTypeId] = useState<string>();
  const [deleteItem, setDeleteItem] = useState<InvitationDocumentResponse>();

  const mediaToken = getMediaToken();
  const identity = useAppSelector(selectIdentity);
  const theme = useTheme();

  const fileRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const documentGroups = useMemo(() => {
    const groups = new Map<string, InvitationDocumentResponse[]>();
    for (const document of data?.documents ?? []) {
      const key = document.requiredCertificate.id ?? '';
      const group = groups.get(key);
      if (group) {
        group.push(document);
      } else {
        groups.set(key, [document]);
      }
    }
    return [...groups.values()];
  }, [data?.documents]);

  const uploadDisabled =
    invitation.state === InvitationState.Archived ||
    !hasRole(identity.roles, [
      UserRole.ManagerWorkplace,
      UserRole.ManagerOsah,
      UserRole.ExternalManager,
      UserRole.ExternalWorker,
      UserRole.ExternalOneTime,
    ]);

  const handleUpload = (item: InvitationDocumentResponse) => () => {
    setUploadModal(item);

    if (item.requiredCertificate?.expiryDateRequired || (item.requiredCertificate?.types?.length ?? 0) > 0) {
      setDateModal(true);
    } else {
      setValidTo(undefined);
      setCertificateTypeId(undefined);
      fileRef.current?.click();
    }
  };

  const handleDateModalClose = (result?: InvitedPersonsDocumentDateModalResult) => {
    setValidTo(result?.date);
    setCertificateTypeId(result?.certificateTypeId);
    setDateModal(false);

    if (result) {
      fileRef.current?.click();
    }
  };

  const handleFileChange = (files: FileList | null) => {
    if (!fileRef.current || !files) {
      return;
    }

    const file = files[0];
    setSelectedFile(file);
    fileRef.current.value = '';
  };

  const handleFileUploaded = async (document: FileUploadMediaResponse) => {
    if (!selectedFile || !uploadModal) {
      return;
    }

    await callWithLock(async () => {
      await invitationService.updateDocument(invitation.id!, uploadModal.requiredCertificate.id!, {
        document: document.id!,
        validTo,
        personId: data!.id!,
        certificateTypeId,
      });
      await reloadInvitation();
    });

    setSelectedFile(null);
  };

  const handleFileCancel = () => {
    setSelectedFile(null);
  };

  const handleApprove = (item: InvitationDocumentResponse) => async () => {
    await callWithLock(async () => {
      await invitationService.approveDocumentCertificate(invitation.id!, {
        entries: [item!.id!],
      });
      await reloadInvitation();
    });
  };

  const handleDelete = (item: InvitationDocumentResponse) => async () => {
    setIsSaving(true);
    await callWithLock(async () => {
      try {
        await mediaApi.unpairDocumentFromEntity(item.id!, 'FROM_INVITATION');
        await reloadInvitation();
      } catch (err) {
        setErrorSnacks(err, enqueueErrorSnackbar);
      } finally {
        setDeleteItem(undefined);
        setIsSaving(false);
      }
    });
  };

  const hasSubRows = (documents: InvitationDocumentResponse[]) => documents.length > 1 || documents.some((document) => !!document.certificateType);

  const handleRenderValidTo = (item: InvitationDocumentResponse) => {
    if (!item.validTo) {
      return null;
    }

    const expired = !invitationCertificateIsExpired(item);

    return (
      <QcsSimpleTableValue label={t('invitation.persons.documentValidTo')}>
        <QcsTypography
          variant="body2"
          sx={{
            color: expired ? theme.palette.error.main : theme.palette.text.secondary,
          }}
        >
          <DateFormat value={parseISO(item.validTo)} />
        </QcsTypography>
      </QcsSimpleTableValue>
    );
  };

  const handleRenderDocumentActions = (item: InvitationDocumentResponse) => {
    const expired = !invitationCertificateIsExpired(item);
    const downloadDisabled = !item.uploadedDocument;
    const canModify = hasRole(identity.roles, [
      UserRole.AdminQcs,
      UserRole.ManagerOsah,
      UserRole.ManagerWorkplace,
      UserRole.ExternalManager,
      UserRole.ExternalWorker,
      UserRole.ExternalOneTime,
    ]);
    const approveDisabled = !item.uploadedDocument || isExternal(identity) || invitation.state === InvitationState.Archived || expired;
    const alreadyApproved = !!item.approvedOn;
    const deleteDisabled = !item.uploadedDocument || invitation.state === InvitationState.Archived;

    return (
      <>
        <ShowImageButton
          tooltipTitle={t('invitation.tabs.tooltips.downloadCertificate')}
          disabled={downloadDisabled}
          color="success"
          documentUrl={`${BASE_URL_WITH_VERSION}${item.uploadedDocument?.path}?token=${mediaToken}`}
        />
        {canModify && (
          <QcsTooltip title={t('invitation.tabs.tooltips.approveCertificate')} placement="top">
            <InnerTooltipWrapper>
              <QcsLoadingButton
                onClick={handleApprove(item)}
                disabled={approveDisabled || alreadyApproved}
                loading={isSaving}
                resourceLoading={[resources.approveDocumentCertificate]}
              >
                <CheckCircleIcon color={approveDisabled ? undefined : alreadyApproved ? 'success' : 'warning'} />
              </QcsLoadingButton>
            </InnerTooltipWrapper>
          </QcsTooltip>
        )}
        {canModify && (
          <QcsTooltip title={t('invitation.tabs.tooltips.deleteCertificate')} placement="top">
            <InnerTooltipWrapper>
              <QcsLoadingButton
                onClick={() => setDeleteItem(item)}
                disabled={deleteDisabled}
                loading={isSaving}
                resourceLoading={[resources.approveDocumentCertificate]}
              >
                <DeleteForeverIcon color={deleteDisabled ? undefined : 'error'} />
              </QcsLoadingButton>
            </InnerTooltipWrapper>
          </QcsTooltip>
        )}
      </>
    );
  };

  const handleRenderSubRow = (item: InvitationDocumentResponse) => (
    <>
      <SubRowInfo>
        <QcsSimpleTableValue label={t('invitation.persons.documentType')}>
          {item.certificateType ? (
            <QcsTypography variant="body2">{getLangNameObj(i18n, item.certificateType)}</QcsTypography>
          ) : (
            <QcsTypography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('invitation.persons.documentTypeNone')}
            </QcsTypography>
          )}
        </QcsSimpleTableValue>
        {handleRenderValidTo(item)}
      </SubRowInfo>
      <SubRowActions>{handleRenderDocumentActions(item)}</SubRowActions>
    </>
  );

  const handleRenderGroup = (documents: InvitationDocumentResponse[]) => {
    const certificate = documents[0].requiredCertificate;
    const groupValid = documents.some((document) => !!document.uploadedDocument && invitationCertificateIsExpired(document));
    const flatDocument = hasSubRows(documents) ? undefined : documents[0];

    return (
      <>
        <QcsTypography variant="body1" sx={{ fontWeight: 500, flex: 1 }}>
          {getLangNameObj(i18n, certificate)}
        </QcsTypography>
        {flatDocument && handleRenderValidTo(flatDocument)}
        <SubRowActions>
          <QcsTooltip title={`${t('invitation.tabs.tooltips.uploadCertificate')} ${t('common.fileMaxSize')}`} placement="top">
            <InnerTooltipWrapper>
              <QcsLoadingButton
                onClick={handleUpload(documents[0])}
                loading={isSaving}
                resourceLoading={[resources.approveDocumentCertificate]}
                disabled={uploadDisabled}
              >
                <CloudUploadIcon color={uploadDisabled ? undefined : groupValid ? 'success' : 'warning'} />
              </QcsLoadingButton>
            </InnerTooltipWrapper>
          </QcsTooltip>
          {flatDocument && handleRenderDocumentActions(flatDocument)}
        </SubRowActions>
      </>
    );
  };

  return (
    <>
      <InvitedPersonsDocumentDateModal
        key={uploadModal?.requiredCertificate.id}
        open={dateModal}
        certificate={uploadModal?.requiredCertificate}
        onClose={handleDateModalClose}
      />
      <DocumentInputUploadModal file={selectedFile} onFileUploaded={handleFileUploaded} onCloseOnError={handleFileCancel} />
      <input type="file" ref={fileRef} onChange={({ target }) => handleFileChange(target.files)} hidden />

      <ConfirmDialog
        onClose={() => setDeleteItem(undefined)}
        onConfirm={deleteItem ? handleDelete(deleteItem) : null}
        title={t('document.certificateDeleteDialog.title')}
        message={t('document.certificateDeleteDialog.message')}
      />

      <QcsModal open={!!data} onClose={onClose}>
        <QcsModalBox>
          <SectionHeader title={t('invitation.persons.documentTitlePerson', { name: `${data?.name ?? ''} ${data?.lastName ?? ''}`.trim() })} />
          <QcsSimpleTable<InvitationDocumentResponse[], InvitationDocumentResponse>
            data={documentGroups}
            getRowKey={(documents) => documents[0].requiredCertificate.id ?? ''}
            renderRow={handleRenderGroup}
            getSubRows={(documents) => (hasSubRows(documents) ? documents.filter((document) => !!document.uploadedDocument) : undefined)}
            getSubRowKey={(document) => document.id ?? ''}
            renderSubRow={handleRenderSubRow}
            renderNoSubRows={() => (
              <QcsTypography variant="body2" sx={{ color: theme.palette.text.secondary, paddingLeft: '1rem' }}>
                {t('invitation.persons.documentNotUploaded')}
              </QcsTypography>
            )}
          />
        </QcsModalBox>
      </QcsModal>
    </>
  );
};
